"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PatientIdentityOpenApiOperations = void 0;
const errors_1 = require("../errors");
function errorCodesFor(endpoint) {
    return errors_1.PatientApiErrorRegistry[endpoint]?.errors.map((entry) => entry.code) ?? [];
}
/**
 * One entry per endpoint contract this stage defines — 20 operations
 * (6 registration variants + demographics + coverage + 2 consent + 2
 * merge + 3 search + 3 protection + 2 identity-resolution). `path` is
 * illustrative REST shape for when a controller layer is eventually
 * built (Stage 4C.4 builds no routes); Application Services already
 * name the exact orchestration each of these would call.
 */
exports.PatientIdentityOpenApiOperations = [
    {
        operationId: 'registerPatient',
        method: 'POST',
        path: '/v1/patients',
        summary: 'Register a new patient (standard, fully-known identity)',
        version: 'v1',
        tags: ['Registration'],
        requestSchemaRef: 'RegisterPatientRequest',
        responseSchemaRef: 'PatientDto',
        errorCodes: errorCodesFor('registerPatient'),
    },
    {
        operationId: 'registerEmergency',
        method: 'POST',
        path: '/v1/patients/emergency',
        summary: 'Register a patient under emergency circumstances',
        version: 'v1',
        tags: ['Registration'],
        requestSchemaRef: 'RegisterEmergencyRequest',
        responseSchemaRef: 'PatientDto',
        errorCodes: errorCodesFor('registerEmergency'),
    },
    {
        operationId: 'registerUnknown',
        method: 'POST',
        path: '/v1/patients/unknown',
        summary: 'Register an unidentified (unknown-identity) patient',
        version: 'v1',
        tags: ['Registration'],
        requestSchemaRef: 'RegisterUnknownRequest',
        responseSchemaRef: 'PatientDto',
        errorCodes: errorCodesFor('registerUnknown'),
    },
    {
        operationId: 'registerNewborn',
        method: 'POST',
        path: '/v1/patients/newborn',
        summary: 'Register a newborn linked to a mother and birth admission',
        version: 'v1',
        tags: ['Registration'],
        requestSchemaRef: 'RegisterNewbornRequest',
        responseSchemaRef: 'PatientDto',
        errorCodes: errorCodesFor('registerNewborn'),
    },
    {
        operationId: 'registerCorporate',
        method: 'POST',
        path: '/v1/patients/corporate',
        summary: 'Register a patient with an employer-sponsored coverage',
        version: 'v1',
        tags: ['Registration'],
        requestSchemaRef: 'RegisterCorporateRequest',
        responseSchemaRef: 'PatientDto',
        errorCodes: errorCodesFor('registerCorporate'),
    },
    {
        operationId: 'registerReferral',
        method: 'POST',
        path: '/v1/patients/referral',
        summary: 'Register a patient referred in from another facility',
        version: 'v1',
        tags: ['Registration'],
        requestSchemaRef: 'RegisterReferralRequest',
        responseSchemaRef: 'PatientDto',
        errorCodes: errorCodesFor('registerReferral'),
    },
    {
        operationId: 'updateDemographics',
        method: 'PATCH',
        path: '/v1/patients/{patientId}/demographics',
        summary: "Update a patient's core demographic fields",
        version: 'v1',
        tags: ['Demographics'],
        requestSchemaRef: 'UpdateDemographicsRequest',
        responseSchemaRef: 'UpdateDemographicsResponseDto',
        errorCodes: errorCodesFor('updateDemographics'),
    },
    {
        operationId: 'updateCoverage',
        method: 'PATCH',
        path: '/v1/patient-coverages/{coverageId}',
        summary: "Update a patient coverage's mutable fields",
        version: 'v1',
        tags: ['Coverage'],
        requestSchemaRef: 'UpdateCoverageRequest',
        responseSchemaRef: 'PatientCoverageDto',
        errorCodes: errorCodesFor('updateCoverage'),
    },
    {
        operationId: 'grantConsent',
        method: 'POST',
        path: '/v1/consents',
        summary: 'Grant a consent for a patient',
        version: 'v1',
        tags: ['Consent'],
        requestSchemaRef: 'GrantConsentRequest',
        responseSchemaRef: 'ConsentDto',
        errorCodes: errorCodesFor('grantConsent'),
    },
    {
        operationId: 'withdrawConsent',
        method: 'POST',
        path: '/v1/consents/{consentId}/withdraw',
        summary: 'Withdraw (revoke) an active consent',
        version: 'v1',
        tags: ['Consent'],
        requestSchemaRef: 'WithdrawConsentRequest',
        responseSchemaRef: 'ConsentDto',
        errorCodes: errorCodesFor('withdrawConsent'),
    },
    {
        operationId: 'mergePatient',
        method: 'POST',
        path: '/v1/patient-merges',
        summary: 'Merge two patient records into one',
        version: 'v1',
        tags: ['Merge'],
        requestSchemaRef: 'MergePatientRequest',
        responseSchemaRef: 'PatientMergeDto',
        errorCodes: errorCodesFor('mergePatient'),
    },
    {
        operationId: 'rollbackMerge',
        method: 'POST',
        path: '/v1/patient-merges/{mergeId}/rollback',
        summary: 'Reverse a completed merge',
        version: 'v1',
        tags: ['Merge'],
        requestSchemaRef: 'RollbackMergeRequest',
        responseSchemaRef: 'PatientMergeDto',
        errorCodes: errorCodesFor('rollbackMerge'),
    },
    {
        operationId: 'searchPatient',
        method: 'POST',
        path: '/v1/patients/search',
        summary: 'Search patients by demographic criteria',
        version: 'v1',
        tags: ['Search'],
        requestSchemaRef: 'SearchPatientRequest',
        responseSchemaRef: 'SearchPatientResponseDto',
        errorCodes: errorCodesFor('searchPatient'),
    },
    {
        operationId: 'advancedSearch',
        method: 'POST',
        path: '/v1/patients/advanced-search',
        summary: 'Search patients across demographics and contact details',
        version: 'v1',
        tags: ['Search'],
        requestSchemaRef: 'AdvancedSearchRequest',
        responseSchemaRef: 'AdvancedSearchResponseDto',
        errorCodes: errorCodesFor('advancedSearch'),
    },
    {
        operationId: 'timeline',
        method: 'GET',
        path: '/v1/patients/{patientId}/timeline',
        summary: "Retrieve a patient's chronological visit/encounter/admission feed",
        version: 'v1',
        tags: ['Search'],
        requestSchemaRef: 'TimelineRequest',
        responseSchemaRef: 'TimelineResponseDto',
        errorCodes: errorCodesFor('timeline'),
    },
    {
        operationId: 'applyRecordProtection',
        method: 'POST',
        path: '/v1/record-protections',
        summary: 'Apply a record protection to a protected entity',
        version: 'v1',
        tags: ['Protection'],
        requestSchemaRef: 'ApplyRecordProtectionRequest',
        responseSchemaRef: 'RecordProtectionDto',
        errorCodes: errorCodesFor('applyRecordProtection'),
    },
    {
        operationId: 'releaseRecordProtection',
        method: 'POST',
        path: '/v1/record-protections/{protectionId}/release',
        summary: 'Release an active record protection',
        version: 'v1',
        tags: ['Protection'],
        requestSchemaRef: 'ReleaseRecordProtectionRequest',
        responseSchemaRef: 'RecordProtectionDto',
        errorCodes: errorCodesFor('releaseRecordProtection'),
    },
    {
        operationId: 'removeRecordProtection',
        method: 'DELETE',
        path: '/v1/record-protections/{protectionId}',
        summary: 'Cancel a record protection request that never activated',
        version: 'v1',
        tags: ['Protection'],
        requestSchemaRef: 'RemoveRecordProtectionRequest',
        responseSchemaRef: 'RecordProtectionDto',
        errorCodes: errorCodesFor('removeRecordProtection'),
    },
    {
        operationId: 'resolveIdentity',
        method: 'POST',
        path: '/v1/identity-resolution/resolve',
        summary: 'Score a pair of patients for possible duplicate identity',
        version: 'v1',
        tags: ['IdentityResolution'],
        requestSchemaRef: 'ResolveIdentityRequest',
        responseSchemaRef: 'IdentityResolutionCandidateDto',
        errorCodes: errorCodesFor('resolveIdentity'),
    },
    {
        operationId: 'reviewIdentity',
        method: 'POST',
        path: '/v1/identity-resolution/candidates/{candidateId}/review',
        summary: 'Record a human decision on an identity resolution candidate',
        version: 'v1',
        tags: ['IdentityResolution'],
        requestSchemaRef: 'ReviewIdentityRequest',
        responseSchemaRef: 'IdentityResolutionCandidateDto',
        errorCodes: errorCodesFor('reviewIdentity'),
    },
];
//# sourceMappingURL=patient-identity.operations.js.map